import { decodeHTMLEntities } from "@simplyk/common";

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const isNil = (value: unknown): value is null | undefined => value === null || value === undefined;

const NAMED_ENTITIES: Record<string, string> = {
  quot: '"',
  apos: "'",
  lt: "<",
  gt: ">",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  middot: "·",
  deg: "°",
  trade: "™",
  reg: "®",
  copy: "©",
};

const decodeEntities = (value: string): string =>
  decodeHTMLEntities(
    value
      .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(Number.parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(Number.parseInt(dec, 10)))
      .replace(/&([a-z]+);/gi, (match, name: string) => NAMED_ENTITIES[name.toLowerCase()] ?? match)
  );

export const cleanText = (value: string | null | undefined): string => {
  if (isNil(value)) {
    return "";
  }
  const withoutScripts = value.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, "");
  const withBreaks = withoutScripts.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6])>/gi, "\n");
  const withoutTags = withBreaks.replace(/<[^>]+>/g, "");
  return decodeEntities(withoutTags)
    .replace(/[\u200B\uFEFF]/g, "")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export const formatCurrency = (value: number | null | undefined): string => {
  if (isNil(value)) {
    return "—";
  }
  return USD_FORMATTER.format(value);
};

export const formatAwardRange = (floor: number | null, ceiling: number | null): string | null => {
  const effectiveFloor = floor === 0 ? null : floor;
  const effectiveCeiling = ceiling === 0 ? null : ceiling;
  if (isNil(effectiveFloor) && isNil(effectiveCeiling)) {
    return null;
  }
  if (!isNil(effectiveFloor) && !isNil(effectiveCeiling) && effectiveFloor !== effectiveCeiling) {
    return `${formatCurrency(effectiveFloor)} – ${formatCurrency(effectiveCeiling)}`;
  }
  if (!isNil(effectiveFloor) && effectiveFloor === effectiveCeiling) {
    return `~${formatCurrency(effectiveFloor)}`;
  }
  if (!isNil(effectiveCeiling)) {
    return `Up to ${formatCurrency(effectiveCeiling)}`;
  }
  if (!isNil(effectiveFloor)) {
    return `From ${formatCurrency(effectiveFloor)}`;
  }
  return null;
};

const USD_COMPACT_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const LARGE_AMOUNT_THRESHOLD = 100_000;
const roundLargeAmount = (value: number): number =>
  value >= LARGE_AMOUNT_THRESHOLD ? Math.round(value / 10_000) * 10_000 : value;

export const formatCompactCurrency = (value: number | null | undefined): string => {
  if (isNil(value)) {
    return "—";
  }
  return USD_COMPACT_FORMATTER.format(roundLargeAmount(value));
};

export const formatAwardRangeCompact = (floor: number | null, ceiling: number | null): string | null => {
  const effectiveFloor = floor === 0 ? null : floor;
  const effectiveCeiling = ceiling === 0 ? null : ceiling;
  if (isNil(effectiveFloor) && isNil(effectiveCeiling)) {
    return null;
  }
  if (!isNil(effectiveFloor) && !isNil(effectiveCeiling) && effectiveFloor !== effectiveCeiling) {
    return `${formatCompactCurrency(effectiveFloor)}–${formatCompactCurrency(effectiveCeiling)}`;
  }
  if (!isNil(effectiveFloor) && effectiveFloor === effectiveCeiling) {
    return `~${formatCompactCurrency(effectiveFloor)}`;
  }
  if (!isNil(effectiveCeiling)) {
    return `Up to ${formatCompactCurrency(effectiveCeiling)}`;
  }
  return `From ${formatCompactCurrency(effectiveFloor)}`;
};

const USD_COMPACT_WHOLE_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 0,
});

export const formatHistoricalAmount = (median: number | null | undefined): string | null => {
  if (isNil(median) || median <= 0) {
    return null;
  }
  return `~${USD_COMPACT_WHOLE_FORMATTER.format(roundLargeAmount(median))}`;
};

export const formatShortDate = (dateInput: string | Date): string => {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const sameYear = date.getUTCFullYear() === new Date().getUTCFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

export const toUtcDay = (value: Date | string): number => {
  const date = new Date(value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const isPastUtcDeadline = (dateInput: string | Date | null): boolean => {
  if (!dateInput) {
    return false;
  }
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return toUtcDay(date) < toUtcDay(new Date());
};

export const formatDeadline = (dateInput: string | Date | null): string => {
  if (!dateInput) {
    return "Rolling";
  }
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) {
    return "Rolling";
  }

  const diffDays = Math.round((toUtcDay(date) - toUtcDay(new Date())) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return "Closed";
  }
  if (diffDays === 0) {
    return "Closes today";
  }
  if (diffDays === 1) {
    return "Closes tomorrow";
  }
  if (diffDays <= 30) {
    return `Closes in ${diffDays} days`;
  }

  return `Closes ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}`;
};
